import React from "react";
import { User } from "firebase/auth";
import { useEffect, useState, useCallback, useRef } from "react";
import PageLoader from "../components/PageLoader";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useBreadcrumbs } from "../context/BreadcrumbContext";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { fetchLeague, saveSelections, setGuestSelections, removeMember } from "../api/leagues";
import { getPredictions, submitPrediction } from "../api/predictions";
import { adminSetGuestPredictions } from "../api/admin";
import { fetchPlayers, fetchClubs } from "../api/players";
import ClubBadge from "../components/ClubBadge";
import CustomSelect from "../components/CustomSelect";
import { getClubBadge } from "../data/clubBadges";
import { getNationalityBadge } from "../data/nationalityBadges";
import ConfirmModal from "../components/ConfirmModal";
import { useExportImage } from "../hooks/useExportImage";
import { safeFilename } from "../utils/shareLeague";
import StatBox from "../components/league/StatBox";
import FPPredictionsView from "../components/league/FPPredictionsView";
import FpDragRow from "../components/league/FpDragRow";
import { computePositionBands } from "../lib/fpBands";
import { computeTargetPace } from "../lib/targetPace";
import FormBar, { FormBarEntry } from "../components/league/FormBar";
import LeagueProgressBar from "../components/league/LeagueProgressBar";
import PlayerCard, { EmptyPlayerCard } from "../components/league/PlayerCard";
import TargetTrackerPanel from "../components/league/TargetTrackerPanel";
import ModeIconSmall from "../components/league/ModeIconSmall";
import { MODE_CONTENT, MODE_COLOR_VAR } from "./createLeague/modeContent";
import StatusDot from "../components/StatusDot";
import {
  card,
  widePageContainer,
  primaryBtn,
  secondaryBtn,
  dangerBtn,
  smallBtn,
  exportBtn,
  tableStyle,
  thStyle,
  tdStyle,
  inputStyle,
  panelHeading,
  badge,
  errorStyle,
} from "../styles";
import { useIsMobile } from "../hooks/useIsMobile";

interface Props {
  user: User;
  isAdmin: boolean;
}

const PAGE_SIZE = 20;

interface LeagueInfo {
  id: string;
  name: string;
  mode: "TARGET" | "FINAL_POSITIONS" | "GAMEWEEK_CHALLENGE";
  status: "PREPARING" | "RUNNING" | "COMPLETED";
  competition: {
    id: string; name: string; season: string; status: string; type?: string;
    gameweeks: { id: string; number: number; name: string; completedAt: string | null }[];
  };
  startGameweek: { id: string; number: number; name: string } | null;
  activeGameweek: { id: string; number: number; name: string } | null;
  myMemberId: string | null;
  isLeagueAdmin: boolean;
  memberCount?: number;
  rules: {
    mode: string;
    target: number;
  };
  leagueGwAverage?: number;
  // Final Positions
  isLocked?: boolean;
  fpClubs?: { id: string; name: string; shortName: string | null; crest: string | null }[];
  latestStandings?: { clubId: string; position: number }[];
  fpScoringConfig?: { topTierSize: number | null; relegationSize: number | null; championEnabled: boolean; topTierLabel: string | null; relegationLabel: string | null };
}

interface MemberDetail {
  id: string;
  userId: string | null;
  status: string;
  displayName: string;
  selections: {
    id: string;
    playerId: string;
    player: { id: string; name: string; club: string; position: string | null; nationality: string | null };
  }[];
  score: number;
  delta: number;
  targetState: "BUST" | "ELIGIBLE" | "INELIGIBLE" | null;
  rank: number | null;
  playerStats: { playerId: string; goals: number; assists: number }[];
  currentGwPlayerStats: { playerId: string; goals: number; assists: number }[];
  chartData: { gameweek: string; gwScore: number; cumulative: number }[];
  leagueStatus: "PREPARING" | "RUNNING" | "COMPLETED";
  rules: {
    mode: string;
    target: number;
  };
}

interface PickerPlayer {
  id: string;
  name: string;
  club: string;
  position: string | null;
  nationality: string | null;
  goals?: number;
}

export default function TeamPage({ user, isAdmin: isSiteAdmin }: Props) {
  const { id: leagueId, memberId } = useParams<{ id: string; memberId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const exportHeaderRef  = useRef<HTMLDivElement>(null);
  const exportSquadRef   = useRef<HTMLDivElement>(null);
  const exportFPRef      = useRef<HTMLDivElement>(null);
  const exportContentRef = useRef<HTMLDivElement>(null);

  const [league, setLeague] = useState<LeagueInfo | null>(null);
  const [member, setMember] = useState<MemberDetail | null>(null);
  useBreadcrumbs(league
    ? (() => {
        const isFP = league.mode === "FINAL_POSITIONS";
        const isMine = league.myMemberId === memberId;
        const label = isFP
          ? (isMine ? "Your Predictions" : `${member?.displayName}'s Predictions`)
          : (isMine ? t("team.title") : t("team.otherTeam", { name: member?.displayName }));
        return [{ label: t("common.home"), to: "/overview" }, { label: league.name, to: `/leagues/${league.id}` }, { label }];
      })()
    : [{ label: t("common.home"), to: "/overview" }]);
  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");

  // Player picker state (PREPARING + own team)
  const [pickerPlayers, setPickerPlayers] = useState<PickerPlayer[]>([]);
  const [pickerTotal, setPickerTotal] = useState(0);
  const [pickerPage, setPickerPage] = useState(1);
  const [pickerSearch, setPickerSearch] = useState("");
  const [pickerClub, setPickerClub] = useState("");
  const [pickerPosition, setPickerPosition] = useState("");
  const [clubs, setClubs] = useState<string[]>([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const searchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Edit state
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [playerCache, setPlayerCache] = useState<Record<string, PickerPlayer>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  const [confirmRemove, setConfirmRemove] = useState(false);
  const [removeLoading, setRemoveLoading] = useState(false);

  // Chart tab
  const [chartTab, setChartTab] = useState<"cumulative" | "per-gw">("cumulative");

  // Share / download
  const [shareDropdownOpen, setShareDropdownOpen] = useState(false);
  const { status: exportStatus, loading: shareLoading, runExport } = useExportImage();

  // Final Positions: this member's predicted standings
  const [fpMemberPrediction, setFpMemberPrediction] = useState<{ clubId: string; position: number }[]>([]);
  type FpItem = { clubId: string; club: { id: string; name: string; shortName: string | null; crest: string | null }; position: number };
  const [fpItems, setFpItems] = useState<FpItem[]>([]);
  const [fpSaving, setFpSaving] = useState(false);
  const [fpSaved] = useState(false);
  // Admin editing guest predictions inline
  const [adminPredItems, setAdminPredItems] = useState<{ clubId: string; position: number }[]>([]);
  const [adminPredSaving, setAdminPredSaving] = useState(false);
  const [adminPredSaved, setAdminPredSaved] = useState(false);
  const adminDragIdx = useRef<number | null>(null);
  const fpDragIdx = useRef<number | null>(null);

  // ── Fetch data ──────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!leagueId || !memberId) return;
    try {
      const [lgData, memData] = await Promise.all([
        fetchLeague(user, leagueId),
        fetch(`${process.env.REACT_APP_API_URL}/leagues/${leagueId}/members/${memberId}`, {
          headers: { Authorization: `Bearer ${await user.getIdToken()}` },
        }).then((r) => { if (!r.ok) throw new Error(); return r.json(); }),
      ]);
      const lgInfo = lgData as unknown as LeagueInfo & { members?: any[] };
      // Capture approved member count for rank display
      if (lgInfo.members) {
        lgInfo.memberCount = lgInfo.members.filter((m: any) => m.status === "APPROVED").length;
      }
      setLeague(lgInfo);
      setMember(memData);
      setSelectedIds(memData.selections.map((s: any) => s.playerId));
      setPlayerCache(Object.fromEntries(memData.selections.map((s: any) => [s.playerId, { id: s.playerId, name: s.player.name, club: s.player.club, position: s.player.position, nationality: s.player.nationality }])));

      // Final Positions: fetch member prediction
      if ((lgData as any).mode === "FINAL_POSITIONS") {
        try {
          if (lgInfo.myMemberId === memberId) {
            const myPrediction: { clubId: string; position: number }[] = (lgData as any).myPrediction ?? [];
            setFpMemberPrediction(myPrediction);
            const fpClubs: { id: string; name: string; shortName: string | null; crest: string | null }[] = (lgData as any).fpClubs ?? [];
            if (myPrediction.length > 0) {
              const sorted = [...myPrediction].sort((a, b) => a.position - b.position);
              setFpItems(sorted.map(p => {
                const club = fpClubs.find(c => c.id === p.clubId) ?? { id: p.clubId, name: p.clubId, shortName: null, crest: null };
                return { clubId: p.clubId, club, position: p.position };
              }));
            } else if (fpClubs.length > 0) {
              setFpItems([...fpClubs].sort((a, b) => a.name.localeCompare(b.name)).map((club, i) => ({ clubId: club.id, club, position: i + 1 })));
            }
          } else {
            const predsData = await getPredictions(user, leagueId!);
            if (predsData.memberPredictions) {
              const found = predsData.memberPredictions.find((m: any) => m.memberId === memberId);
              const preds = found?.predictions.map((p: any) => ({ clubId: p.clubId, position: p.position })) ?? [];
              setFpMemberPrediction(preds);
              // Admin editing guest: pre-fill with existing prediction or alphabetical order
              const clubs: { id: string; name: string }[] = (lgData as any).fpClubs ?? [];
              if (preds.length > 0) {
                setAdminPredItems([...preds].sort((a, b) => a.position - b.position));
              } else if (clubs.length > 0) {
                setAdminPredItems(
                  [...clubs].sort((a, b) => a.name.localeCompare(b.name)).map((c, i) => ({ clubId: c.id, position: i + 1 }))
                );
              }
            }
          }
        } catch { /* predictions hidden pre-lock — ok */ }
      }
    } catch {
      setError(t("team.errors.failedToLoad"));
    } finally {
      setLoading(false);
    }
  }, [leagueId, memberId, user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  // ── Fetch clubs for picker ───────────────────────────────────────────────
  useEffect(() => {
    if (!shouldLoadPicker() || !league) return;
    fetchClubs(user, league.competition.id)
      .then(setClubs)
      .catch(() => {});
  }, [league]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch picker players (debounced search) ──────────────────────────────
  const fetchPickerPlayers = useCallback(async (search: string, club: string, position: string, page: number) => {
    if (!league) return;
    setPickerLoading(true);
    try {
      const result = await fetchPlayers(user, {
        competitionId: league.competition.id,
        page,
        pageSize: PAGE_SIZE,
        search: search || undefined,
        club: club || undefined,
        position: position || undefined,
      });
      setPickerPlayers(result.players);
      setPickerTotal(result.total);
      setPlayerCache(prev => ({ ...prev, ...Object.fromEntries(result.players.map((p: PickerPlayer) => [p.id, p])) }));
    } catch {
      /* ignore */
    } finally {
      setPickerLoading(false);
    }
  }, [league, user]);

  useEffect(() => {
    if (!shouldLoadPicker()) return;
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => {
      setPickerPage(1);
      fetchPickerPlayers(pickerSearch, pickerClub, pickerPosition, 1);
    }, 300);
  }, [pickerSearch, pickerClub, pickerPosition]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!shouldLoadPicker()) return;
    fetchPickerPlayers(pickerSearch, pickerClub, pickerPosition, pickerPage);
  }, [pickerPage]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (shouldLoadPicker()) {
      fetchPickerPlayers("", "", "", 1);
    }
  }, [league]); // eslint-disable-line react-hooks/exhaustive-deps


  // ── Helpers ──────────────────────────────────────────────────────────────
  function isOwnTeamPreparing(): boolean {
    return !!(league && member && league.myMemberId === memberId && league.status === "PREPARING");
  }

  function isOwnTeam(): boolean {
    return !!(league && league.myMemberId === memberId);
  }

  /** True whenever the player picker should be loaded (own team or admin editing a guest) */
  function shouldLoadPicker(): boolean {
    if (!league || !member) return false;
    if (isOwnTeamPreparing()) return true;
    return (
      (league.isLeagueAdmin || isSiteAdmin) &&
      member.userId === null &&
      member.leagueStatus === "PREPARING"
    );
  }

  const playerSlots = 4;

  // ── Save team ─────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!leagueId || !league || !member) return;
    // Check player count first so the user sees the right error
    if (selectedIds.length !== playerSlots) {
      setSaveError(t("team.errors.tooManyPlayers", { slots: playerSlots }));
      return;
    }
    setSaving(true);
    setSaveError("");
    // Admin saving on behalf of a guest uses the member-specific endpoint
    const adminSavingGuest =
      (league.isLeagueAdmin || isSiteAdmin) &&
      member.userId === null &&
      member.leagueStatus === "PREPARING" &&
      league.myMemberId !== memberId;
    try {
      if (adminSavingGuest && memberId) {
        await setGuestSelections(user, leagueId, memberId, selectedIds);
      } else {
        await saveSelections(user, leagueId, selectedIds);
      }
      navigate(`/leagues/${leagueId}`);
    } catch {
      setSaveError(t("team.errors.failedToSave"));
    } finally {
      setSaving(false);
    }
  };

  // ── Toggle player in selection ──────────────────────────────────────────
  const togglePlayer = (playerId: string) => {
    setSelectedIds((prev) => {
      if (prev.includes(playerId)) return prev.filter((id) => id !== playerId);
      if (prev.length >= playerSlots) return prev;
      return [...prev, playerId];
    });
  };

  const addPlayerFromPool = (player: PickerPlayer) => {
    if (selectedIds.includes(player.id) || selectedIds.length >= playerSlots) return;
    setSelectedIds(prev => [...prev, player.id]);
    setPlayerCache(prev => ({ ...prev, [player.id]: player }));
  };

  // ── Claim team ──────────────────────────────────────────────────────────
  // ── FP inline prediction drag + save ───────────────────────────────────────
  const moveFpItem = (from: number, to: number) => {
    if (to < 0 || to >= fpItems.length) return;
    setFpItems(prev => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next.map((item, idx) => ({ ...item, position: idx + 1 }));
    });
  };
  const moveAdminPredItem = (from: number, to: number) => {
    if (to < 0 || to >= adminPredItems.length) return;
    setAdminPredItems(prev => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next.map((item, idx) => ({ ...item, position: idx + 1 }));
    });
  };
  const handleFpSave = async () => {
    if (!leagueId) return;
    setFpSaving(true);
    try {
      const payload = fpItems.map((item, i) => ({ clubId: item.clubId, position: i + 1 }));
      await submitPrediction(user, leagueId, payload);
      navigate(`/leagues/${leagueId}`);
    } catch { /* silent */ } finally {
      setFpSaving(false);
    }
  };

  // ── Remove from league ───────────────────────────────────────────────────
  const handleRemove = async () => {
    if (!leagueId || !memberId) return;
    setRemoveLoading(true);
    try {
      await removeMember(user, leagueId, memberId);
      navigate(`/leagues/${leagueId}`);
    } catch {
      setError(t("common.somethingWentWrong"));
    } finally {
      setRemoveLoading(false);
    }
  };

  // ── pickerRef for scroll-to-picker ─────────────────────────────────────────
  const pickerRef = useRef<HTMLDivElement>(null);

  // ── Share / download ────────────────────────────────────────────────────────
  const handleTeamShare = async (mode: "simple" | "full", action: "download" | "copy") => {
    if (!member || !league) return;
    setShareDropdownOpen(false);
    const isFP = league.mode === "FINAL_POSITIONS";
    const simpleEl = isFP ? exportFPRef.current : exportSquadRef.current;
    const els = mode === "full" ? [exportHeaderRef.current, exportContentRef.current] : [simpleEl];
    await runExport(els, action, `${safeFilename(member.displayName)}.png`);
  };

  // ── Render ───────────────────────────────────────────────────────────────
  if (loading) return <PageLoader />;
  if (error)   return <div style={{ padding: "2rem", color: "#c00" }}>{error}</div>;
  if (!league || !member) return null;

  const isGuest        = member.userId === null;
  const isAdmin        = league.isLeagueAdmin || isSiteAdmin;
  const leagueStatus   = member.leagueStatus;
  const isAdminEditingGuest = isAdmin && isGuest && leagueStatus === "PREPARING";
  const canEdit        = leagueStatus === "PREPARING" && (isOwnTeamPreparing() || isAdminEditingGuest);
  const isFP           = league.mode === "FINAL_POSITIONS";
  const fpBands = isFP
    ? computePositionBands({
        clubCount: (league.fpClubs ?? []).length,
        topTierSize: league.fpScoringConfig?.topTierSize ?? null,
        relegationSize: league.fpScoringConfig?.relegationSize ?? null,
        championEnabled: league.fpScoringConfig?.championEnabled ?? true,
        topTierLabel: league.fpScoringConfig?.topTierLabel ?? null,
        relegationLabel: league.fpScoringConfig?.relegationLabel ?? null,
        fallbackLabels: { champion: t("prediction.bands.champion"), top: t("prediction.bands.top"), mid: t("prediction.bands.mid"), relegation: t("prediction.bands.relegation") },
      })
    : [];
  const isStarted      = leagueStatus !== "PREPARING";
  const totalPages     = Math.ceil(pickerTotal / PAGE_SIZE);
  const approvedMemberCount = league.memberCount ?? 0;
  const showAssists    = false;

  const canShare = leagueStatus !== "PREPARING" && (league.mode === "TARGET" || isFP);

  // GW counts
  const completedGwCount = league.competition.gameweeks.filter(
    gw => gw.number >= (league.startGameweek?.number ?? 1) && gw.completedAt
  ).length;
  const totalGwCount = league.competition.gameweeks.filter(gw => gw.number > 0).length;

  // Totals for stat boxes
  const totalGoals = member.playerStats.reduce((sum, ps) => sum + ps.goals, 0);
  const target     = league.rules.target ?? 0;
  const vsTarget   = computeTargetPace({
    totalGoals, target,
    gameweeks: league.competition.gameweeks,
    startGwNumber: league.startGameweek?.number ?? 1,
  }) ?? 0;
  const vsAvg      = league.leagueGwAverage != null ? member.delta - league.leagueGwAverage : null;

  // Status box logic
  const isBust = member.targetState === "BUST";
  const statusLabel = isBust ? "Bust"
    : member.targetState === "INELIGIBLE" ? "Waiting"
    : "On Track";

  // Form bar data (last 5 completed GWs from chartData)
  const formHistory: FormBarEntry[] = member.chartData
    .slice(0, completedGwCount)
    .slice(-5)
    .map((e, i, arr) => ({
      gwNumber: completedGwCount - arr.length + i + 1,
      gwName: e.gameweek,
      score: e.gwScore,
    }));

  // Chart data with optional required pace line (TARGET mode)
  const chartForRender = (() => {
    const synthetic = { gameweek: "", gwScore: 0 as number | null, cumulative: 0 as number | null, required: 0 };
    if (!isStarted && league.mode === "TARGET" && totalGwCount > 0) {
      // PREPARING: show target line only, no real data
      return [
        synthetic,
        ...Array.from({ length: totalGwCount }, (_, i) => ({
          gameweek: `GW${i + 1}`,
          gwScore: null as number | null,
          cumulative: null as number | null,
          required: target * (i + 1) / totalGwCount,
        })),
      ];
    }
    if (member.chartData.length === 0) return [];

    let lastActiveIdx = -1;
    for (let i = 0; i < member.chartData.length; i++) {
      if (member.chartData[i].gwScore > 0) lastActiveIdx = i;
    }
    const trimmedChart = [
      synthetic,
      ...member.chartData.map((entry, i) => ({
        gameweek: entry.gameweek,
        gwScore: i <= lastActiveIdx ? entry.gwScore : null as number | null,
        cumulative: i <= lastActiveIdx ? entry.cumulative : null as number | null,
        required: league.mode === "TARGET" && totalGwCount > 0
          ? target * (i + 1) / totalGwCount
          : undefined,
      })),
    ];
    return trimmedChart;
  })();

  const metaItems: React.ReactNode[] = [
    league.competition.name,
    <span key="mode" style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem" }}>
      <ModeIconSmall mode={league.mode} color={MODE_COLOR_VAR[league.mode]} />
      <span style={{ fontSize: "0.62rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: MODE_COLOR_VAR[league.mode] }}>
        {t(`${MODE_CONTENT[league.mode].keyRoot}.label`)}
      </span>
    </span>,
    league.competition.season,
  ];

  return (
    <div className="sg-fade-in" style={{ ...widePageContainer, padding: isMobile ? "1rem 0" : "1.5rem 0" }}>

      {/* ── Header card ── */}
      <div ref={exportHeaderRef} style={{ ...card, marginBottom: "1.25rem" }}>
        {/* Metadata row */}
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.5rem", flexWrap: "wrap" }}>
          {metaItems.map((s, i) => (
            <React.Fragment key={i}>
              {typeof s === "string"
                ? <span style={{ fontSize: "0.62rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--color-text-dim)" }}>{s}</span>
                : s}
              <span style={{ color: "var(--color-border)", fontSize: "0.75rem" }}>·</span>
            </React.Fragment>
          ))}
          <StatusDot status={leagueStatus} />
        </div>

        {/* Title + buttons */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "1rem", marginBottom: "0.4rem", flexWrap: "wrap" }}>
          <div style={{ minWidth: 0 }}>
            {isFP ? (
              <h1 style={{ margin: "0 0 0.25rem", fontFamily: "var(--font-display)", fontSize: "1.75rem", fontWeight: 800, color: "var(--color-text)" }}>
                {isOwnTeam() ? "Your Predictions" : `${member.displayName}'s Predictions`}
              </h1>
            ) : (
              <div style={{ display: "flex", alignItems: "baseline", gap: "0.75rem", flexWrap: "wrap", marginBottom: "0.25rem" }}>
                <h1 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: "1.75rem", fontWeight: 800, color: "var(--color-text)" }}>
                  {isOwnTeam() ? t("team.title") : t("team.otherTeam", { name: member.displayName })}
                </h1>
                {member.rank != null && isStarted && (
                  <span style={{ fontFamily: "var(--font-display)", fontSize: "1.5rem", fontWeight: 700, color: "var(--color-accent)" }}>
                    #{member.rank}
                    <span style={{ fontSize: "1rem", color: "var(--color-text-muted)", fontWeight: 400 }}> / {approvedMemberCount}</span>
                  </span>
                )}
              </div>
            )}
            {canEdit && !isFP && league.mode === "TARGET" ? (
              <p style={{ margin: 0, fontSize: "0.88rem", color: "var(--color-text-muted)" }}>
                Pick <strong style={{ color: "var(--color-accent)" }}>{playerSlots} players</strong> who will score{" "}
                <strong style={{ color: "var(--color-accent)" }}>{target} goals</strong> between them by GW{totalGwCount}.
              </p>
            ) : (
              <p style={{ margin: 0, fontSize: "0.82rem", color: "var(--color-text-muted)" }}>
                {member.displayName}
                {!isFP && league.mode === "TARGET" && ` · Target mode · ${target} goals by GW${totalGwCount}`}
                {isGuest && <span style={{ ...badge, background: "var(--color-surface-alt)", color: "var(--color-text-muted)", marginLeft: "0.5rem" }}>Guest</span>}
              </p>
            )}
          </div>

          {/* Action buttons */}
          <div data-no-export style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexShrink: 0 }}>
            {canShare && (
              <div style={{ position: "relative" }}>
                <button style={exportBtn} onClick={() => setShareDropdownOpen(v => !v)} disabled={shareLoading} title={t("team.exportBtn")}>
                  {shareLoading
                    ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" strokeDasharray="32" strokeDashoffset="12"/></svg>
                    : <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 2v11M6 9l4 4 4-4"/><path d="M3 14v2a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-2"/></svg>}
                </button>
                {shareDropdownOpen && (
                  <>
                    <div style={{ position: "fixed", inset: 0, zIndex: 99 }} onClick={() => setShareDropdownOpen(false)} />
                    <div style={{ position: "absolute", right: 0, top: "calc(100% + 6px)", background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: 10, boxShadow: "0 8px 32px var(--color-shadow)", zIndex: 100, minWidth: 190, overflow: "hidden" }}>
                      <div style={{ padding: "0.35rem 1rem 0.2rem", fontSize: "0.6rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--color-text-dim)" }}>Simple</div>
                      {(["download", "copy"] as const).map(action => (
                        <button key={action} onClick={() => handleTeamShare("simple", action)} style={{ display: "block", width: "100%", padding: "0.5rem 1rem", background: "none", border: "none", textAlign: "left", fontSize: "0.85rem", color: "var(--color-text)", cursor: "pointer" }}>
                          {action === "download" ? "Download image" : "Copy as image"}
                        </button>
                      ))}
                      <div style={{ margin: "0.2rem 1rem", borderTop: "1px solid var(--color-border-subtle)" }} />
                      <div style={{ padding: "0.35rem 1rem 0.2rem", fontSize: "0.6rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--color-text-dim)" }}>Full</div>
                      {(["download", "copy"] as const).map(action => (
                        <button key={action} onClick={() => handleTeamShare("full", action)} style={{ display: "block", width: "100%", padding: "0.5rem 1rem", background: "none", border: "none", textAlign: "left", fontSize: "0.85rem", color: "var(--color-text)", cursor: "pointer" }}>
                          {action === "download" ? "Download image" : "Copy as image"}
                        </button>
                      ))}
                    </div>
                  </>
                )}
                {exportStatus && (
                  <div style={{ position: "absolute", right: 0, top: "calc(100% + 6px)", zIndex: 100, whiteSpace: "nowrap", padding: "0.4rem 0.7rem", borderRadius: 8, fontSize: "0.78rem", fontWeight: 600, background: "var(--color-surface)", border: `1px solid ${exportStatus.type === "error" ? "var(--color-danger)" : "var(--color-border)"}`, color: exportStatus.type === "error" ? "var(--color-danger)" : "var(--color-text)", boxShadow: "0 8px 32px var(--color-shadow)" }}>
                    {exportStatus.message}
                  </div>
                )}
              </div>
            )}
            <Link to={`/leagues/${leagueId}`} style={{ ...secondaryBtn, textDecoration: "none", fontSize: "0.85rem" }}>← League</Link>

            {canEdit && !isFP && (
              selectedIds.length >= playerSlots ? (
                <button style={primaryBtn} onClick={handleSave} disabled={saving}>
                  {saving ? t("common.saving") : t("team.saveTeam")}
                </button>
              ) : (
                <button style={{ ...secondaryBtn, fontSize: "0.85rem" }} onClick={() => pickerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}>
                  Select {playerSlots - selectedIds.length} more player{playerSlots - selectedIds.length !== 1 ? "s" : ""}
                </button>
              )
            )}

            {isFP && leagueStatus === "PREPARING" && isOwnTeam() && !league.isLocked && (
              <button style={primaryBtn} onClick={handleFpSave} disabled={fpSaving || fpItems.length === 0}>
                {fpSaving ? "Saving…" : fpSaved ? "Saved ✓" : "Save Predictions"}
              </button>
            )}

          </div>
        </div>

        {/* Progress bar */}
        <div style={{ marginTop: "0.75rem" }}>
          <LeagueProgressBar
            totalGws={totalGwCount}
            currentGwNumber={completedGwCount}
            startGwNumber={league.startGameweek?.number ?? 1}
            status={league.status}
          />
        </div>

        {/* Pre-season stat boxes for TARGET mode (build context) */}
        {canEdit && league.mode === "TARGET" && (
          <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
            <StatBox label={t("team.statBoxes.targetLabel")} value={target} sub={t("team.statBoxes.goalsByGw", { n: totalGwCount })} accent borderColor="var(--color-accent)" />
            <StatBox label={t("team.statBoxes.squadSize")} value={`${selectedIds.length}/${playerSlots}`} sub={t("team.statBoxes.playersSelected")} borderColor="var(--color-accent)" />
            <StatBox label={t("overview.table.mode")} value={t("createLeague.mode.TARGET.label")} sub={t("team.statBoxes.highestTotalWins")} borderColor="var(--color-accent)" />
          </div>
        )}

        {/* Stat boxes (TARGET only, running/completed only) */}
        {!isFP && isStarted && (
          <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
            {league.mode === "TARGET" ? (
              <>
                <StatBox label={t("league.statBoxes.target.goals")} value={completedGwCount > 0 ? `${totalGoals}/${target}` : "—"} sub={t("league.statBoxes.target.towardTarget")} locked={!isStarted} accent borderColor="var(--color-accent)" />
                <StatBox label={t("team.statBoxes.gwScore")} value={isStarted ? (member.delta !== 0 ? <span style={{ color: member.delta > 0 ? "var(--color-success)" : "var(--color-danger)" }}>{member.delta > 0 ? `+${member.delta}` : `${member.delta}`}</span> : "—") : "—"} sub={t("team.statBoxes.goalsThisGw")} locked={!isStarted} mobileHide borderColor="var(--color-accent)" />
                <StatBox label={t("league.statBoxes.target.vsTarget")} value={isStarted ? (vsTarget > 0 ? <span style={{ color: "var(--color-success)" }}>{`+${vsTarget.toFixed(1)}`}</span> : <span style={{ color: "var(--color-danger)" }}>{vsTarget.toFixed(1)}</span>) : "—"} sub={t("league.statBoxes.target.aheadBehindPace")} locked={!isStarted} mobileHide borderColor="var(--color-accent)" />
                <StatBox label={t("league.statBoxes.form")} value={isStarted && formHistory.length > 0 ? <FormBar history={formHistory} /> : undefined} sub={t("league.statBoxes.last5Gws")} locked={!isStarted || formHistory.length === 0} borderColor="var(--color-accent)" />
                <StatBox label={t("league.statBoxes.target.status")} value={isStarted ? statusLabel : "—"} sub={isStarted ? t("league.statBoxes.target.goalsCount", { n: totalGoals }) : undefined} locked={!isStarted} danger={isBust} accent={!isBust && member.targetState !== "INELIGIBLE"} borderColor="var(--color-accent)" />
              </>
            ) : (
              <>
                <StatBox label={t("league.statBoxes.score")} value={member.score} sub={league.activeGameweek?.name ? t("league.statBoxes.gwCumulative", { gw: league.activeGameweek.name }) : t("league.statBoxes.cumulative")} locked={!isStarted} accent borderColor="var(--color-accent)" />
                <StatBox label={t("team.statBoxes.gwScore")} value={isStarted ? (member.delta !== 0 ? <span style={{ color: member.delta > 0 ? "var(--color-success)" : "var(--color-danger)" }}>{member.delta > 0 ? `+${member.delta}` : `${member.delta}`}</span> : "—") : "—"} sub={t("team.statBoxes.vsLastGw")} locked={!isStarted} borderColor="var(--color-accent)" />
                <StatBox label={t("team.statBoxes.vsAvgLabel")} value={vsAvg != null && isStarted ? (vsAvg > 0 ? <span style={{ color: "var(--color-success)" }}>{`+${vsAvg.toFixed(1)}`}</span> : <span style={{ color: "var(--color-danger)" }}>{vsAvg.toFixed(1)}</span>) : "—"} sub={t("team.statBoxes.thisGwVsAvg")} locked={!isStarted || vsAvg == null} mobileHide borderColor="var(--color-accent)" />
                <StatBox label={t("league.statBoxes.form")} value={isStarted && formHistory.length > 0 ? <FormBar history={formHistory} /> : undefined} sub={t("league.statBoxes.last5Gws")} locked={!isStarted || formHistory.length === 0} borderColor="var(--color-accent)" />
                <StatBox label={t("league.statBoxes.avgPerGw")} value={isStarted && completedGwCount > 0 ? (member.score / completedGwCount).toFixed(2) : "—"} sub={t("league.statBoxes.seasonToDate")} locked={!isStarted} mobileHide borderColor="var(--color-accent)" />
              </>
            )}
          </div>
        )}
      </div>

      {/* ── Exportable content (squad, chart, trophies) ── */}
      <div ref={exportContentRef}>

      {/* ── Not submitted (RUNNING/COMPLETED with no selections) ── */}
      {isStarted && !isFP && member.selections.length === 0 && (
        <div style={{ ...card, marginBottom: "1.25rem" }}>
          <p style={{ margin: 0, color: "var(--color-text-muted)", fontSize: "0.9rem" }}>
            {isOwnTeam()
              ? "Your team wasn't submitted before the league started and won't be included in the standings."
              : `${member.displayName} didn't submit a team before the league started.`}
          </p>
        </div>
      )}

      {/* ── Squad section (TARGET) ── */}
      {/* PREPARING + viewing another member's team: picks stay hidden until the competition starts */}
      {!isFP && !isStarted && !canEdit && !isOwnTeam() && (
        <div style={{ ...card, marginBottom: "1.25rem" }}>
          <p style={{ margin: 0, color: "var(--color-text-muted)", fontSize: "0.85rem" }}>{t("team.preparingOtherNotice")}</p>
        </div>
      )}
      {!isFP && (isStarted ? member.selections.length > 0 : (canEdit || isOwnTeam())) && (
        <div ref={exportSquadRef} style={{ ...card, padding: 0, overflow: "hidden", marginBottom: "1.25rem" }}>
          {canEdit ? (
            <>
              <div style={{ padding: "1.1rem 1.25rem 0.4rem", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <h2 style={{ ...panelHeading, margin: 0 }}>
                  Your Squad
                </h2>
                <span style={{ fontSize: "0.72rem", color: "var(--color-text-dim)" }}>Slots are interchangeable — pick any {playerSlots} players</span>
              </div>
              <div style={{ padding: "0.6rem 1.25rem", borderBottom: "1px solid var(--color-border-subtle)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: "0.6rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--color-text-dim)", marginBottom: "0.35rem" }}>
                    Squad — {selectedIds.length}/{playerSlots} picked
                  </div>
                  <div style={{ display: "flex", gap: "0.25rem" }}>
                    {Array.from({ length: playerSlots }).map((_, i) => (
                      <div key={i} style={{ height: 4, flex: 1, borderRadius: 2, background: i < selectedIds.length ? "var(--color-accent)" : "var(--color-border)" }} />
                    ))}
                  </div>
                </div>
                {selectedIds.length < playerSlots && (
                  <span style={{ fontSize: "0.72rem", fontWeight: 600, color: "oklch(0.80 0.15 55)", whiteSpace: "nowrap", flexShrink: 0 }}>
                    ● {playerSlots - selectedIds.length} slot{playerSlots - selectedIds.length !== 1 ? "s" : ""} open
                  </span>
                )}
              </div>
            </>
          ) : (
            <div style={{ padding: "1.1rem 1.25rem 0.4rem" }}>
              <h2 style={{ ...panelHeading, margin: 0 }}>
                {isOwnTeam() ? "Your Squad" : "Squad"}
              </h2>
            </div>
          )}
          <div style={{ padding: "1rem 1.25rem" }}>
            {canEdit ? (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: "0.75rem" }}>
                {selectedIds.map(id => {
                  const p = playerCache[id];
                  if (!p) return null;
                  return (
                    <PlayerCard
                      key={id}
                      player={p}
                      stats={{ goals: 0, assists: 0 }}
                      showAssists={false}
                      hideStats={true}
                      canRemove={true}
                      onRemove={() => togglePlayer(id)}
                    />
                  );
                })}
                {Array.from({ length: Math.max(0, playerSlots - selectedIds.length) }).map((_, i) => (
                  <EmptyPlayerCard key={`empty-${i}`} slotNumber={selectedIds.length + i + 1} onAdd={() => pickerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })} />
                ))}
              </div>
            ) : (
              (() => {
                // Build "current team" from player stats
                const selectionMap = new Map(member.selections.map((s) => [s.playerId, s.player]));
                const currentPlayers: { playerId: string; player: { id: string; name: string; club: string; position: string | null; nationality: string | null }; goals: number; assists: number }[] = [];
                for (const ps of member.playerStats) {
                  const fromSel = selectionMap.get(ps.playerId);
                  if (fromSel) {
                    currentPlayers.push({ playerId: ps.playerId, player: fromSel, goals: ps.goals, assists: ps.assists });
                  }
                }
                return (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: "0.75rem" }}>
                    {currentPlayers.map(({ playerId, player, goals, assists }) => {
                      const gwStat = (member.currentGwPlayerStats ?? []).find(ps => ps.playerId === playerId);
                      return (
                        <div key={playerId} style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
                          <PlayerCard
                            player={player}
                            stats={{ goals, assists }}
                            currentGwStats={gwStat}
                            showAssists={showAssists}
                            canRemove={false}
                            onRemove={() => {}}
                          />
                        </div>
                      );
                    })}
                  </div>
                );
              })()
            )}
          </div>
        </div>
      )}

      {/* ── Chart ── */}
      {!isFP && isStarted && member.selections.length > 0 && chartForRender.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: (league.mode === "TARGET" && !isMobile) ? "1fr 300px" : "1fr", gap: "1.25rem", alignItems: "start", marginBottom: "1.25rem" }}>

          {/* Chart */}
          <section style={{ ...card, padding: 0, overflow: "hidden" }}>
            <div style={{ padding: "1.1rem 1.25rem 0.4rem", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <h2 style={{ ...panelHeading, margin: 0 }}>
                Score Progression
              </h2>
              {isStarted && (
                <div style={{ display: "flex", gap: "0.25rem" }}>
                  {(["cumulative", "per-gw"] as const).map(tab => (
                    <button key={tab} onClick={() => setChartTab(tab)} style={{
                      padding: "0.2rem 0.6rem", fontSize: "0.75rem", borderRadius: 6, border: "none", cursor: "pointer", fontFamily: "var(--font-body)",
                      background: chartTab === tab ? "var(--color-accent)" : "var(--color-surface-alt)",
                      color: chartTab === tab ? "var(--color-btn-primary-text)" : "var(--color-text-muted)",
                    }}>
                      {tab === "cumulative" ? "Cumulative" : "Per GW"}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div style={{ padding: "1rem" }}>
              {(!isStarted && league.mode === "TARGET") ? (
                <div>
                  <p style={{ margin: "0 0 0.75rem", fontSize: "0.78rem", color: "var(--color-text-muted)" }}>
                    Target pace — {target} goals by GW{totalGwCount}
                  </p>
                  <ResponsiveContainer width="100%" height={180}>
                    <AreaChart data={chartForRender} margin={{ top: 4, right: 4, bottom: 20, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-subtle)" />
                      <XAxis dataKey="gameweek" tick={{ fontSize: 9, fill: "var(--color-text-dim)" }} angle={-45} textAnchor="end" interval={Math.floor(totalGwCount / 6)} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 9, fill: "var(--color-text-dim)" }} axisLine={false} tickLine={false} domain={[0, target + 2]} />
                      <Area type="monotone" dataKey="required" stroke="oklch(0.75 0.17 60)" strokeWidth={1.5} strokeDasharray="5 3" fill="oklch(0.75 0.17 60 / 0.05)" dot={false} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              ) : chartTab === "cumulative" ? (
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={chartForRender} margin={{ top: 4, right: 4, bottom: 20, left: 0 }}>
                    <defs>
                      <linearGradient id="tp-grad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="var(--color-accent)" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="var(--color-accent)" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-subtle)" />
                    <XAxis dataKey="gameweek" tick={{ fontSize: 9, fill: "var(--color-text-dim)" }} angle={-45} textAnchor="end" interval={Math.floor(chartForRender.length / 6)} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 9, fill: "var(--color-text-dim)" }} axisLine={false} tickLine={false} domain={[0, "auto"]} />
                    <Tooltip contentStyle={{ background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: 8, fontSize: "0.78rem" }} />
                    <Area type="monotone" dataKey="cumulative" name={member.displayName} stroke="var(--color-accent)" strokeWidth={2} fill="url(#tp-grad)" dot={false} connectNulls={false} />
                    {league.mode === "TARGET" && (
                      <Area type="monotone" dataKey="required" name="Required pace" stroke="oklch(0.75 0.17 60)" strokeWidth={1.5} strokeDasharray="5 3" fill="none" dot={false} />
                    )}
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={chartForRender.filter(d => d.gameweek !== "")} margin={{ top: 4, right: 4, bottom: 20, left: 0 }}>
                    <defs>
                      <linearGradient id="tp-bar-grad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="var(--color-accent)" stopOpacity={0.9} />
                        <stop offset="95%" stopColor="var(--color-accent)" stopOpacity={0.5} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-subtle)" vertical={false} />
                    <XAxis dataKey="gameweek" tick={{ fontSize: 9, fill: "var(--color-text-dim)" }} angle={-45} textAnchor="end" interval={Math.floor(chartForRender.length / 6)} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 9, fill: "var(--color-text-dim)" }} axisLine={false} tickLine={false} domain={[0, "auto"]} />
                    <Tooltip contentStyle={{ background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: 8, fontSize: "0.78rem" }} cursor={false} />
                    <Bar dataKey="gwScore" name={member.displayName} fill="url(#tp-bar-grad)" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </section>

          {/* Right panel — TARGET only */}
          {league.mode === "TARGET" && (
            <TargetTrackerPanel
              totalGoals={totalGoals}
              target={target}
              completedGwCount={completedGwCount}
              totalGwCount={totalGwCount}
              targetState={member.targetState}
            />
          )}
        </div>
      )}

      {/* ── Player picker (PREPARING, own team, TARGET) ── */}
      {canEdit && !isFP && (
        <div data-no-export ref={pickerRef} style={{ ...card, padding: 0, overflow: "hidden", marginBottom: "1.25rem" }}>
          <div style={{ padding: "1.1rem 1.25rem 0.4rem", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap" }}>
            <h2 style={{ ...panelHeading, margin: 0 }}>
              Player Pool
            </h2>
            <input
              style={{ ...inputStyle, fontSize: "0.88rem", width: 200 }}
              placeholder="Search players…"
              value={pickerSearch}
              onChange={(e) => { setPickerSearch(e.target.value); setPickerPage(1); }}
            />
          </div>
          {/* Filters */}
          <div style={{ padding: "0.6rem 1.25rem", borderBottom: "1px solid var(--color-border-subtle)", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <span style={{ fontSize: "0.62rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--color-text-dim)", width: 56, flexShrink: 0 }}>Position</span>
              <div style={{ display: "flex", gap: "0.25rem" }}>
                {(["", "GK", "DF", "MF", "FW"] as const).map(pos => (
                  <button
                    key={pos || "all"}
                    onClick={() => { setPickerPosition(pos); setPickerPage(1); }}
                    style={{
                      padding: "3px 10px", borderRadius: 99, fontSize: "0.72rem", fontWeight: 600, cursor: "pointer",
                      border: pickerPosition === pos ? "1px solid var(--color-accent)" : "1px solid var(--color-border)",
                      background: pickerPosition === pos ? "var(--color-accent-glow)" : "transparent",
                      color: pickerPosition === pos ? "var(--color-accent)" : "var(--color-text-muted)",
                      fontFamily: "var(--font-body)",
                    }}
                  >
                    {pos || "All"}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <span style={{ fontSize: "0.62rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--color-text-dim)", width: 56, flexShrink: 0 }}>Team</span>
              <CustomSelect
                value={pickerClub}
                onChange={(v) => { setPickerClub(v); setPickerPage(1); }}
                options={clubs.map((c) => ({ value: c, label: c }))}
                placeholder={t("team.playerPicker.allClubs")}
                style={{ minWidth: 140 }}
              />
            </div>
            {saveError && <p style={{ ...errorStyle, margin: 0 }}>{saveError}</p>}
          </div>
          {/* Player table */}
          <table style={{ ...tableStyle, opacity: pickerLoading ? 0.4 : 1, transition: "opacity 0.15s" }}>
              <thead>
                <tr>
                  <th style={thStyle}>{t("team.playerPicker.columns.name")}</th>
                  {!isMobile && <th style={thStyle}>{t("team.playerPicker.columns.club")}</th>}
                  <th style={thStyle}>{t("team.playerPicker.columns.position")}</th>
                  {!isMobile && <th style={thStyle}>{t("team.playerPicker.columns.nationality")}</th>}
                  <th style={{ ...thStyle, width: 80 }} />
                </tr>
              </thead>
              <tbody>
                {pickerPlayers.map((p) => {
                  const sel = selectedIds.includes(p.id);
                  const full = !sel && selectedIds.length >= playerSlots;
                  const isInternational = league.competition.type === "INTERNATIONAL";
                  return (
                    <tr key={p.id} style={{ background: sel ? "var(--color-accent-glow)" : undefined }}>
                      <td style={tdStyle}>
                        <span style={{ color: "var(--color-accent)", fontWeight: 500 }}>{p.name}</span>
                        {isMobile && (
                          <div style={{ display: "flex", alignItems: "center", gap: "0.3rem", marginTop: 2 }}>
                            <ClubBadge config={isInternational ? getNationalityBadge(p.nationality) : getClubBadge(p.club)} size={13} title={isInternational ? (p.nationality ?? p.club) : p.club} />
                            <span style={{ fontSize: "0.7rem", color: "var(--color-text-dim)" }}>{p.club}</span>
                          </div>
                        )}
                      </td>
                      {!isMobile && (
                        <td style={tdStyle}>
                          <span style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem" }}>
                            <ClubBadge config={isInternational ? getNationalityBadge(p.nationality) : getClubBadge(p.club)} size={18} title={isInternational ? (p.nationality ?? p.club) : p.club} />
                            {p.club}
                          </span>
                        </td>
                      )}
                      <td style={tdStyle}>{p.position ?? "—"}</td>
                      {!isMobile && <td style={tdStyle}>{p.nationality ?? "—"}</td>}
                      <td style={{ ...tdStyle, textAlign: "right", width: 90, whiteSpace: "nowrap" }}>
                        {sel ? (
                          <button onClick={() => togglePlayer(p.id)} style={{ ...smallBtn, fontSize: "0.72rem", width: 72, color: "var(--color-danger)", border: "1px solid var(--color-danger)" }}>Remove</button>
                        ) : (
                          <button onClick={() => addPlayerFromPool(p)} disabled={full} style={{ ...smallBtn, fontSize: "0.72rem", width: 72, opacity: full ? 0.4 : 1, cursor: full ? "not-allowed" : "pointer" }}>+ Add</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          {totalPages > 1 && (
            <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", padding: "0.75rem 1.25rem", borderTop: "1px solid var(--color-border)" }}>
              <button style={smallBtn} disabled={pickerPage <= 1} onClick={() => setPickerPage(p => p - 1)}>{t("admin.pagination.previous")}</button>
              <span style={{ fontSize: "0.85rem", color: "var(--color-text-muted)" }}>{t("admin.pagination.pageOf", { page: pickerPage, total: totalPages })}</span>
              <button style={smallBtn} disabled={pickerPage >= totalPages} onClick={() => setPickerPage(p => p + 1)}>{t("admin.pagination.next")}</button>
            </div>
          )}
        </div>
      )}

      {/* ── FP section (Final Positions) ── */}
      {isFP && (() => {
        const clubs = league.fpClubs ?? [];
        const standings = league.latestStandings ?? [];
        const N = clubs.length;
        const bonus = 5;
        const actualMap = new Map(standings.map((s) => [s.clubId, s.position]));

        // PREPARING: show prediction viz (own) or locked notice (others)
        if (leagueStatus === "PREPARING") {
          if (isOwnTeam() && !league.isLocked) {
            return (
              <div style={{ ...card, padding: 0, overflow: "hidden", marginBottom: "1.25rem" }}>
                {/* Header */}
                <div style={{ padding: "1.1rem 1.25rem 0.85rem" }}>
                  <h2 style={{ ...panelHeading, margin: "0 0 0.4rem" }}>Rank all {N} clubs</h2>
                  <p style={{ margin: 0, fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
                    Drag any club by its handle to reorder, or tap # to jump straight to a position.
                  </p>
                </div>
                {/* Drag list */}
                <div>
                  {fpItems.map((item, i) => (
                    <FpDragRow
                      key={item.clubId}
                      index={i}
                      total={fpItems.length}
                      label={item.club.name}
                      band={fpBands[i]}
                      dragIndexRef={fpDragIdx}
                      move={moveFpItem}
                      isLast={i === fpItems.length - 1}
                      isMobile={isMobile}
                    />
                  ))}
                </div>
              </div>
            );
          }
          return (
            <div style={{ ...card, marginBottom: "1.25rem" }}>
              <p style={{ margin: 0, color: "var(--color-text-muted)", fontSize: "0.85rem" }}>{t("team.preparingOtherNotice")}</p>
            </div>
          );
        }

        // RUNNING/COMPLETED: parallel coordinates visualization
        if (fpMemberPrediction.length === 0) {
          return (
            <div style={{ ...card, marginBottom: "1.25rem" }}>
              <p style={{ margin: 0, color: "var(--color-text-muted)", fontSize: "0.9rem" }}>
                {isOwnTeam() ? t("league.noPrediction") : "No prediction submitted yet."}
              </p>
              {isOwnTeam() && !league.isLocked && (
                <Link to={`/leagues/${leagueId}/predict`} style={{ ...primaryBtn, textDecoration: "none", display: "inline-block", marginTop: "0.75rem" }}>{t("league.setPrediction")}</Link>
              )}
            </div>
          );
        }
        return (
          <div ref={exportFPRef} style={{ marginBottom: "1.25rem" }}>
            <FPPredictionsView
              clubs={clubs}
              predictions={fpMemberPrediction}
              actuals={actualMap}
              N={N}
              bonus={bonus}
              memberScore={member.score}
              memberRank={member.rank}
              memberCount={approvedMemberCount}
              chartData={member.chartData.slice(0, completedGwCount)}
              isOwnTeam={isOwnTeam()}
              leagueStatus={leagueStatus}
              bands={fpBands}
            />
          </div>
        );
      })()}

      {/* ── Admin: edit guest FP predictions ── */}
      {isAdminEditingGuest && isFP && adminPredItems.length > 0 && (
        <div data-no-export style={{ ...card, padding: 0, overflow: "hidden", marginBottom: "1.25rem" }}>
          <div style={{ padding: "1.1rem 1.5rem 0.4rem", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <h2 style={{ ...panelHeading, margin: 0 }}>Set Prediction for {member.displayName}</h2>
            <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
              {adminPredSaved && <span style={{ fontSize: "0.8rem", color: "var(--color-success)" }}>✓ Saved</span>}
              <button style={primaryBtn} disabled={adminPredSaving} onClick={async () => {
                if (!leagueId || !memberId) return;
                setAdminPredSaving(true);
                try {
                  await adminSetGuestPredictions(user, leagueId, memberId, adminPredItems);
                  setFpMemberPrediction([...adminPredItems]);
                  setAdminPredSaved(true);
                  setTimeout(() => setAdminPredSaved(false), 2500);
                } catch { /* ignore */ } finally { setAdminPredSaving(false); }
              }}>
                {adminPredSaving ? "Saving..." : "Save Prediction"}
              </button>
            </div>
          </div>
          {adminPredItems.map((item, i) => {
            const club = (league.fpClubs ?? []).find((c) => c.id === item.clubId);
            return (
              <FpDragRow
                key={item.clubId}
                index={i}
                total={adminPredItems.length}
                label={club?.name ?? item.clubId}
                band={fpBands[i]}
                dragIndexRef={adminDragIdx}
                move={moveAdminPredItem}
                isLast={i === adminPredItems.length - 1}
                isMobile={isMobile}
              />
            );
          })}
        </div>
      )}

      </div>{/* end exportContentRef */}

      {/* ── Admin: remove from league ── */}
      {isAdmin && !isOwnTeam() && leagueStatus === "RUNNING" && !isFP && (
        <div data-no-export style={{ borderTop: "1px solid var(--color-border)", paddingTop: "1rem", marginTop: "0.5rem" }}>
          {confirmRemove ? (
            <ConfirmModal title={t("team.confirmRemoveFromLeague.title")} message={t("team.confirmRemoveFromLeague.message", { name: member.displayName })} confirmLabel={t("team.removeFromLeague")} confirming={removeLoading} onConfirm={handleRemove} onCancel={() => setConfirmRemove(false)} />
          ) : (
            <button style={dangerBtn} onClick={() => setConfirmRemove(true)}>{t("team.removeFromLeague")}</button>
          )}
        </div>
      )}
    </div>
  );
}
